import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import app, { authenticateFirestoreClient, defaultAuth, loginVerifierAuth } from "../../firebase";
import { validateActiveAccount } from "../../authStatus";
import LoginErrorMessage from "../common/LoginErrorMessage";
import { assertRoleLoginAllowed, useRoleSessionState } from "../../auth/roleSession";

const db = getFirestore(app);

function getFriendlyAuthError(error) {
  switch (error?.code) {
    case "auth/invalid-credential":
    case "auth/invalid-login-credentials":
    case "auth/wrong-password":
    case "auth/user-not-found":
    case "auth/user-disabled":
      return "Incorrect email or password.";
    case "auth/invalid-email":
      return "Enter a valid email address.";
    case "auth/too-many-requests":
      return "Too many failed attempts. Try again later.";
    case "auth/network-request-failed":
    case "unavailable":
    case "deadline-exceeded":
      return "Service temporarily unavailable. Check your connection and try again.";
    case "auth/role-already-active":
      return error.message;
    default:
      return "Unable to sign in. Please try again.";
  }
}

export default function FinancesLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { loading: sessionLoading, user: sessionUser } = useRoleSessionState("Finance");

  useEffect(() => {
    if (!sessionLoading && sessionUser) navigate("/finances/dashboard", { replace: true });
  }, [navigate, sessionLoading, sessionUser]);

  if (sessionLoading) return null;
  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    let persistentAuth = null;
    let createdPersistentSession = false;
    let loginSucceeded = false;
    let firestoreAuthUid = null;
    try {
      const normalizedEmail = email.trim().toLowerCase();
      const verifiedCredential = await signInWithEmailAndPassword(loginVerifierAuth, normalizedEmail, password);
      const uid = verifiedCredential.user.uid;
      await authenticateFirestoreClient(normalizedEmail, password, uid);
      firestoreAuthUid = uid;
      const verifiedProfile = await getDoc(doc(db, "users", uid));
      if (!verifiedProfile.exists()) {
        setError("Unable to sign in. Please try again.");
        return;
      }

      const verifiedData = verifiedProfile.data();
      const validation = validateActiveAccount(verifiedData, "Account");
      if (!validation.active) {
        setError(validation.message);
        return;
      }
      const financeRoles = ["Treasurer", "Financial Officer"];
      if (!financeRoles.includes(verifiedData.role)) {
        setError("You are signing in to a different role. Please choose the correct login page for your role from the home page.");
        return;
      }

      persistentAuth = await assertRoleLoginAllowed("Finance", uid);
      if (persistentAuth.currentUser?.uid !== uid) {
        const persistentCredential = await signInWithEmailAndPassword(persistentAuth, normalizedEmail, password);
        createdPersistentSession = true;
        if (persistentCredential.user.uid !== uid) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
          setError("Unable to sign in. Please try again.");
          return;
        }
      }

      const userDoc = await getDoc(doc(db, "users", uid));
      if (!userDoc.exists()) {
        setError("Unable to sign in. Please try again.");
        return;
      }

      const persistentData = userDoc.data();
      const persistentValidation = validateActiveAccount(persistentData, "Account");
      if (!persistentValidation.active) {
        setError(persistentValidation.message);
        if (persistentAuth.currentUser?.uid === uid) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
        }
        return;
      }

      if (financeRoles.includes(persistentData.role)) {
        navigate("/finances/dashboard");
      } else {
        setError("You are signing in to a different role. Please choose the correct login page for your role from the home page.");
        if (createdPersistentSession) {
          await signOut(persistentAuth);
          createdPersistentSession = false;
        }
        return;
      }
      loginSucceeded = true;
    } catch (err) {
      setError(getFriendlyAuthError(err));
    } finally {
      try {
        if (!loginSucceeded && firestoreAuthUid && defaultAuth.currentUser?.uid === firestoreAuthUid) {
          await signOut(defaultAuth);
        }
      } catch (cleanupError) {
        console.error("Failed to clean up unsuccessful Firestore login session", cleanupError);
      }
      try {
        if (createdPersistentSession && !loginSucceeded && persistentAuth) {
          await signOut(persistentAuth);
        }
      } catch (cleanupError) {
        console.error("Failed to clean up unsuccessful Finance login session", cleanupError);
      }
      try {
        await signOut(loginVerifierAuth);
      } catch (cleanupError) {
        console.error("Failed to clean up login verifier session", cleanupError);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "linear-gradient(90deg, #009966 0%, #155c2b 100%)",
      padding: 20,
      boxSizing: "border-box"
    }}>
      <div style={{
        width: "100%",
        maxWidth: 420,
        background: "#fff",
        borderRadius: 24,
        boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
        padding: 36,
        boxSizing: "border-box"
      }}>
        <h2 style={{ textAlign: "center", fontWeight: 800, color: "#155c2b", marginBottom: 18 }}>EnrollEase</h2>

        <form onSubmit={handleLogin}>
          <label style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Email</label>
          <input
            type="text"
            placeholder="you@school.edu.ph"
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={{ width: "100%", marginBottom: 12, padding: "12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" }}
            autoComplete="username"
          />

          <label style={{ display: "block", fontWeight: 600, color: "#155c2b", marginBottom: 6 }}>Password</label>
          <input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
            style={{ width: "100%", marginBottom: 12, padding: "12px 14px", borderRadius: 8, border: "1px solid #d1d5db", boxSizing: "border-box" }}
            autoComplete="current-password"
          />
          <div style={{ textAlign: "right", marginBottom: 12 }}>
            <Link to="/forgot-password?scope=Finance&returnTo=%2Ffinances" style={{ color: "#155c2b", fontSize: 14 }}>Forgot Password?</Link>
          </div>

          <LoginErrorMessage message={error} />

          <button
            type="submit"
            disabled={loading}
            style={{
              width: "100%",
              padding: "12px 0",
              background: loading ? "#ccc" : "#155c2b",
              color: "#fff",
              border: "none",
              borderRadius: 10,
              fontWeight: 700,
              fontSize: 16,
              cursor: loading ? "not-allowed" : "pointer",
              marginBottom: 12
            }}
          >
            {loading ? "Signing in..." : "LOG IN"}
          </button>
        </form>
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <Link to="/" style={{ color: "#155c2b", fontSize: 14 }}>Return to home</Link>
        </div>
      </div>
    </div>
  );
}
